import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Sparkles, Share2, Download,
  Check, Heart, BellRing, Star, Pin, Edit2, Trash2, X, Plus, Megaphone, Image as ImageIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, BACKEND_URL, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";

/* ============================================================
   COMPATIBILITY TEST — cinematic flow
   ============================================================ */
export function CompatibilityPage() {
  const { user } = useAuth();
  const [stage, setStage] = useState("intro"); // intro | role | answering | done
  const [partnerRole, setPartnerRole] = useState("partner_one");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [results, setResults] = useState(null);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    api.get("/compatibility/questions").then((r) => setQuestions(r.data || [])).catch(() => {});
    api.get("/compatibility/results").then((r) => {
      if (r.data?.ready) setResults(r.data);
    }).catch(() => {});
  }, []);

  async function submit() {
    const ordered = questions.map((q) => answers[q.index] ?? -1);
    if (ordered.some((a) => a < 0)) return toast.error("Answer all questions first");
    try {
      await api.post("/compatibility/answers", { partner_role: partnerRole, answers: ordered });
      toast.success("Answers saved!");
      const { data } = await api.get("/compatibility/results");
      if (data?.ready) {
        setResults(data);
        setStage("done");
      } else {
        setStage("waiting");
      }
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function generateInsights() {
    setInsightsBusy(true);
    try {
      const { data } = await api.post("/compatibility/insights");
      setInsights(data.text);
    } catch (err) { toast.error(formatApiError(err)); }
    setInsightsBusy(false);
  }

  function shareCard() {
    const url = `${BACKEND_URL}/api/compatibility/share-card.png`;
    if (navigator.share) {
      navigator.share({ title: "Our Locked In Score", url }).catch(() => {});
    } else {
      window.open(url, "_blank");
    }
  }

  // RESULTS view
  if (results && stage !== "answering") {
    return (
      <div className="space-y-10" data-testid="compatibility-results">
        <div className="text-center py-10 bg-gradient-to-b from-[#C8A96B]/15 via-transparent to-transparent rounded-3xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Locked In Score</p>
          <motion.p
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-8xl md:text-9xl text-[var(--ink)]"
          >
            {results.overall}%
          </motion.p>
          <p className="font-script text-5xl text-[var(--gold)] mt-3">{results.couple_emoji} {results.couple_type}</p>
          <p className="text-[var(--muted)] mt-2">{results.couple_names}</p>

          <div className="flex gap-3 justify-center mt-7">
            <button onClick={shareCard} data-testid="compat-share" className="btn-gold"><Share2 className="w-4 h-4" /> Share our score</button>
            <a href={`${BACKEND_URL}/api/compatibility/share-card.png`} download="locked-in-score.png" className="btn-outline-gold"><Download className="w-4 h-4" /> Download card</a>
          </div>
        </div>

        {/* Category breakdown */}
        <div>
          <p className="font-serif text-2xl mb-4">Category breakdown</p>
          <div className="space-y-3">
            {results.categories.map((c) => (
              <div key={c.key} className="bg-white border border-[var(--line)] rounded-2xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-sm">{c.label}</p>
                  <p className="text-sm font-semibold text-[var(--gold)]">{c.score}%</p>
                </div>
                <div className="h-2 bg-[var(--bg-warm)] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${c.score}%` }}
                    transition={{ duration: 0.8 }}
                    className="h-full bg-gradient-to-r from-[var(--gold)] to-amber-400"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI Insights */}
        <div className="bg-[var(--ink)] text-white rounded-3xl p-7 md:p-9">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI Relationship Insights</p>
          {insights ? (
            <p className="whitespace-pre-line text-sm md:text-base leading-relaxed">{insights}</p>
          ) : (
            <button onClick={generateInsights} disabled={insightsBusy} data-testid="compat-insights" className="btn-gold">
              {insightsBusy ? "Generating…" : "Reveal Our Story"} <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>

        <button onClick={() => { setStage("role"); setStep(0); setAnswers({}); }} className="btn-outline-gold w-full justify-center" data-testid="compat-retake">
          Take it again
        </button>
      </div>
    );
  }

  // ANSWERING flow
  if (stage === "answering" && questions.length) {
    const q = questions[step];
    const total = questions.length;
    const pct = Math.round(((step + 1) / total) * 100);
    return (
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="compat-answering">
        <div className="flex items-center justify-between text-sm">
          <p className="text-[var(--muted)]">Question {step + 1} of {total}</p>
          <p className="text-[var(--gold)] font-medium uppercase tracking-[0.18em] text-[10px]">{q.category_label}</p>
        </div>
        <div className="h-1.5 bg-[var(--bg-warm)] rounded-full overflow-hidden">
          <motion.div animate={{ width: `${pct}%` }} className="h-full bg-[var(--gold)]" />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={q.index}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35 }}
            className="bg-white border border-[var(--line)] rounded-3xl p-7 md:p-9"
          >
            <p className="font-serif text-2xl md:text-3xl mb-6">{q.text}</p>
            <div className="space-y-2">
              {q.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => setAnswers({ ...answers, [q.index]: i })}
                  data-testid={`compat-q${q.index}-opt${i}`}
                  className={`w-full text-left p-4 rounded-2xl border text-sm transition ${
                    answers[q.index] === i
                      ? "border-[var(--gold)] bg-[#C8A96B]/10 text-[var(--ink)]"
                      : "border-[var(--line)] bg-white hover:border-[var(--gold)]"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="btn-outline-gold disabled:opacity-30" data-testid="compat-prev">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {step === total - 1 ? (
            <button onClick={submit} className="btn-gold" data-testid="compat-submit">
              See our score <Sparkles className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => setStep(step + 1)}
              disabled={answers[q.index] == null}
              className="btn-gold disabled:opacity-30"
              data-testid="compat-next"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // WAITING (one partner done)
  if (stage === "waiting") {
    return (
      <div className="text-center py-16">
        <Heart className="w-10 h-10 mx-auto text-[var(--gold)] mb-4" />
        <p className="font-serif text-3xl mb-2">Locked in.</p>
        <p className="text-[var(--muted)] max-w-md mx-auto">Your answers are saved. Once your partner takes the test too, your full report unlocks.</p>
        <button onClick={() => { setStage("role"); setStep(0); setAnswers({}); }} className="btn-outline-gold mt-6">Take as the other partner</button>
      </div>
    );
  }

  // ROLE pick
  if (stage === "role") {
    return (
      <div className="max-w-md mx-auto space-y-6">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Locked In Compatibility</p>
          <h1 className="font-serif font-light text-3xl md:text-4xl">Who's answering?</h1>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { v: "partner_one", label: "Partner 1" },
            { v: "partner_two", label: "Partner 2" },
          ].map((r) => (
            <button key={r.v} onClick={() => { setPartnerRole(r.v); setStage("answering"); }} data-testid={`compat-role-${r.v}`} className={`bg-white border rounded-2xl p-6 text-center hover:border-[var(--gold)] transition ${partnerRole === r.v ? "border-[var(--gold)]" : "border-[var(--line)]"}`}>
              <Heart className="w-6 h-6 mx-auto text-[var(--gold)] mb-3" />
              <p className="font-serif text-xl">{r.label}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // INTRO
  return (
    <div className="space-y-10" data-testid="compatibility-intro">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Locked In Compatibility</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl leading-tight">Test your love story.</h1>
        <p className="text-[var(--muted)] mt-3 max-w-xl">40 cinematic questions. 8 categories. One Locked In Score — and an AI-generated insight just for you two.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { e: "✨", t: "8 categories", d: "Love, money, family, conflict & more" },
          { e: "🎬", t: "Cinematic flow", d: "One question at a time, swipe-style" },
          { e: "💍", t: "Couple Type", d: "From Soulmate to Power Couple" },
          { e: "🤖", t: "AI Insights", d: "Claude Sonnet 4.5 writes your story" },
        ].map((c) => (
          <div key={c.t} className="bg-white border border-[var(--line)] rounded-2xl p-5">
            <p className="text-3xl mb-3">{c.e}</p>
            <p className="font-medium">{c.t}</p>
            <p className="text-xs text-[var(--muted)] mt-1">{c.d}</p>
          </div>
        ))}
      </div>

      <button onClick={() => setStage("role")} data-testid="compat-start" className="btn-gold">
        Start the test <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ============================================================
   ANNOUNCEMENTS — public feed + creator
   ============================================================ */
export function PublicAnnouncementsPage() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/announcements").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);
  const featured = items.filter((a) => a.is_featured);
  const rest = items.filter((a) => !a.is_featured);
  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <div className="container-wide max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="announcements-feed">
        <div className="text-center mb-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Locked In News</p>
          <h1 className="font-serif font-light text-4xl md:text-6xl">Announcements</h1>
        </div>
        {featured.length > 0 && (
          <div className="mb-12">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-4 flex items-center gap-2"><Star className="w-3 h-3" /> Featured</p>
            <div className="space-y-5">
              {featured.map((a) => <AnnouncementCard key={a.announcement_id} a={a} large />)}
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-5">
          {rest.map((a) => <AnnouncementCard key={a.announcement_id} a={a} />)}
        </div>
        {items.length === 0 && (
          <div className="text-center py-20"><p className="text-[var(--muted)]">No announcements yet. Stay tuned.</p></div>
        )}
      </div>
    </div>
  );
}

function AnnouncementCard({ a, large = false }) {
  const shareUrl = `${BACKEND_URL}/api/announcements/${a.announcement_id}/share-card.png`;
  const [shared, setShared] = useState(false);
  function share() {
    if (navigator.share) {
      navigator.share({ title: a.title, text: a.body, url: shareUrl }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
      toast.success("Share link copied");
    }
  }
  return (
    <article className={`bg-white border ${a.is_featured ? "border-[var(--gold)]" : "border-[var(--line)]"} rounded-3xl overflow-hidden`} data-testid={`announcement-${a.announcement_id}`}>
      {a.image_url && (
        <div className={`${large ? "aspect-[16/7]" : "aspect-[16/9]"} bg-[var(--bg-warm)] overflow-hidden`}>
          <img src={a.image_url} alt={a.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-2">
          {a.is_featured && <span className="text-[var(--gold)] flex items-center gap-1"><Pin className="w-3 h-3" /> Featured</span>}
          {a.is_admin_post && <span>Locked In Forever</span>}
          {!a.is_admin_post && <span>{a.author_name}</span>}
          <span>· {new Date(a.created_at).toLocaleDateString()}</span>
        </div>
        <h2 className={`font-serif ${large ? "text-3xl md:text-4xl" : "text-2xl"} mb-3`}>{a.title}</h2>
        <p className="text-sm text-[var(--muted)] mb-4 whitespace-pre-line">{a.body}</p>
        <div className="flex items-center gap-3">
          {a.link_url && (
            a.link_url.startsWith("http") ? (
              <a href={a.link_url} target="_blank" rel="noreferrer" className="btn-gold text-xs">Read more →</a>
            ) : (
              <Link to={a.link_url} className="btn-gold text-xs">Read more →</Link>
            )
          )}
          <button onClick={share} data-testid={`share-${a.announcement_id}`} className="btn-outline-gold text-xs">
            <Share2 className="w-3 h-3" /> {shared ? "Copied" : "Share"}
          </button>
        </div>
      </div>
    </article>
  );
}

/* ============================================================
   ANNOUNCEMENTS — Dashboard manager (couple) + Admin tab use
   ============================================================ */
export function AnnouncementsManagerPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    title: "", body: "", image_url: "", link_url: "",
    audience: "public", is_featured: false, is_published: true,
  });

  function load() { api.get("/announcements/mine/list").then((r) => setItems(r.data || [])).catch(() => {}); }
  useEffect(() => { load(); }, []);

  async function uploadImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    try {
      const { data: r } = await api.post("/storage/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND_URL}/api/files/${r.storage_path}`;
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function save(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body required");
    try {
      if (editing) await api.patch(`/announcements/${editing}`, form);
      else await api.post("/announcements", form);
      toast.success(editing ? "Updated" : "Published");
      setOpen(false); setEditing(null);
      setForm({ title: "", body: "", image_url: "", link_url: "", audience: "public", is_featured: false, is_published: true });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  function startEdit(a) {
    setEditing(a.announcement_id);
    setForm({
      title: a.title || "", body: a.body || "", image_url: a.image_url || "", link_url: a.link_url || "",
      audience: a.audience || "public", is_featured: !!a.is_featured, is_published: !!a.is_published,
    });
    setOpen(true);
  }
  async function remove(id) {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`); load();
  }

  return (
    <div className="space-y-8" data-testid="announcements-manager">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Announcements</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl">{isAdmin ? "Platform announcements" : "Tell the world."}</h1>
          <p className="text-[var(--muted)] mt-2">Each announcement gets a luxury share card with the official logo.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/announcements" className="btn-outline-gold text-xs">View public feed</Link>
          <button onClick={() => { setOpen(true); setEditing(null); }} data-testid="ann-new" className="btn-gold">
            <Plus className="w-4 h-4" /> New announcement
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-[var(--bg-warm)] rounded-3xl">
          <Megaphone className="w-7 h-7 mx-auto text-[var(--gold)] mb-2" />
          <p className="text-sm text-[var(--muted)]">No announcements yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((a) => (
            <div key={a.announcement_id} className="bg-white border border-[var(--line)] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
                  {a.is_featured && <span className="text-[var(--gold)] flex items-center gap-1"><Pin className="w-3 h-3" /> Featured</span>}
                  <span>{a.is_published ? "Published" : "Draft"}</span>
                  <span>· {a.audience}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(a)} className="text-[var(--muted)] hover:text-[var(--gold)]"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(a.announcement_id)} className="text-[var(--muted)] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="font-serif text-xl">{a.title}</p>
              <p className="text-xs text-[var(--muted)] mt-1 line-clamp-3">{a.body}</p>
              <div className="flex gap-2 mt-3">
                <a href={`${BACKEND_URL}/api/announcements/${a.announcement_id}/share-card.png`} target="_blank" rel="noreferrer" className="btn-outline-gold text-xs"><ImageIcon className="w-3 h-3" /> Share card</a>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <p className="font-serif text-2xl">{editing ? "Edit announcement" : "New announcement"}</p>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Headline" className="w-full border border-[var(--line)] rounded-xl px-3 py-2" data-testid="ann-title" />
            <textarea required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Tell the story…" className="w-full border border-[var(--line)] rounded-xl px-3 py-2" data-testid="ann-body" />
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Banner image</label>
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Paste image URL or upload below" className="w-full border border-[var(--line)] rounded-xl px-3 py-2" data-testid="ann-image-url" />
              <label className="text-xs text-[var(--gold)] cursor-pointer flex items-center gap-2 mt-1">
                <ImageIcon className="w-3 h-3" /> Upload from device
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} />
              </label>
              {form.image_url && (
                <img src={form.image_url} alt="" className="w-full max-h-40 object-cover rounded-xl mt-2" />
              )}
            </div>
            <input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="CTA link (optional, e.g. /podcast)" className="w-full border border-[var(--line)] rounded-xl px-3 py-2" />
            {isAdmin && (
              <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full border border-[var(--line)] rounded-xl px-3 py-2">
                <option value="public">Public feed</option>
                <option value="platform">Platform-wide (admin)</option>
              </select>
            )}
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                Feature this announcement (pinned to top)
              </label>
            )}
            <button type="submit" data-testid="ann-save" className="btn-gold w-full justify-center">
              {editing ? "Update" : "Publish"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
